<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Список кейсов</h2>
    </div>

    <!-- Поиск кейсов и кнопка на одном уровне -->
    <div class="mb-3">
      <div class="row align-items-center">
        <div class="col-md-6">
          <div class="input-group">
            <span class="input-group-text">
              🔍
            </span>
            <input 
              type="text" 
              class="form-control" 
              placeholder="Поиск по названию, описанию или вопросам..." 
              v-model="searchQuery"
              @input="onSearchInput"
            >
            <button 
              v-if="searchQuery" 
              class="btn btn-outline-secondary" 
              type="button" 
              @click="clearSearch"
            >
              ✕
            </button>
          </div>
        </div>
        <div class="col-md-6">
          <div class="d-flex justify-content-end">
            <button @click="openCreateModal" class="btn btn-primary">
              <i class="bi bi-plus-circle"></i> Добавить кейс
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Информация о пагинации -->
    <div v-if="pagination.total > 0" class="mb-3">
      <small class="text-muted">
        <span v-if="searchQuery">
          Найдено {{ pagination.total }} кейсов по запросу "{{ searchQuery }}"
          (показано {{ (pagination.page - 1) * pagination.size + 1 }}-{{ Math.min(pagination.page * pagination.size, pagination.total) }})
        </span>
        <span v-else>
          Показано {{ (pagination.page - 1) * pagination.size + 1 }}-{{ Math.min(pagination.page * pagination.size, pagination.total) }} 
          из {{ pagination.total }} кейсов
        </span>
      </small>
    </div>

    <div v-if="cases.length > 0">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>ID</th>
            <th>Название</th>
            <th>Описание</th>
            <th>Бесплатный</th>
            <th class="text-end">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="caseItem in cases" :key="caseItem.id">
            <td>{{ caseItem.id }}</td>
            <td>{{ caseItem.title }}</td>
            <td class="text-truncate" style="max-width: 300px;" :title="caseItem.description">
              {{ truncateText(caseItem.description, 50) }}
            </td>
            <td>{{ caseItem.is_free ? "Да" : "Нет" }}</td>
            <td class="text-end">
              <div class="d-flex justify-content-end gap-2">
                <router-link 
                  :to="{ name: 'CaseDetails', params: { id: caseItem.id } }" 
                  class="btn btn-info btn-sm" 
                  title="Подробнее"
                >
                  <i class="bi bi-eye"></i>
                </router-link>
                <button 
                  class="btn btn-warning btn-sm" 
                  @click="editCase(caseItem)"
                  title="Редактировать"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <button 
                  class="btn btn-danger btn-sm" 
                  @click="confirmDelete(caseItem)"
                  title="Удалить"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Пагинация -->
      <nav v-if="pagination.pages > 1" aria-label="Пагинация кейсов">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: pagination.page === 1 }">
            <button class="page-link" @click="changePage(pagination.page - 1)" :disabled="pagination.page === 1">
              Предыдущая
            </button>
          </li>
          
          <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === pagination.page }">
            <button class="page-link" @click="changePage(page)">{{ page }}</button>
          </li>
          
          <li class="page-item" :class="{ disabled: pagination.page === pagination.pages }">
            <button class="page-link" @click="changePage(pagination.page + 1)" :disabled="pagination.page === pagination.pages">
              Следующая
            </button>
          </li>
        </ul>
      </nav>

      <!-- Выбор размера страницы -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <div>
          <label for="pageSize" class="form-label me-2">Кейсов на странице:</label>
          <select id="pageSize" class="form-select d-inline-block w-auto" v-model="pagination.size" @change="changePageSize">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>
    </div>

    <div v-else-if="!loading" class="text-center py-5">
      <p class="text-muted">Кейсы не найдены</p>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
    </div>

    <!-- Модалка для создания и редактирования кейса -->
    <div class="modal fade" id="caseModal" tabindex="-1" aria-labelledby="caseModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingCase ? 'Редактировать кейс' : 'Создать кейс' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitCase">
              <div class="mb-3">
                <label for="title" class="form-label">Название</label>
                <input v-model="caseData.title" type="text" class="form-control" id="title" required />
              </div>
              <div class="mb-3">
                <label for="description" class="form-label">Описание - отправляется в бота после выбора кейса</label>
                <textarea v-model="caseData.description" class="form-control" id="description" required></textarea>
              </div>
              <div class="mb-3">
                <label for="description" class="form-label">Короткое описание - выводится после выбора кейса</label>
                <textarea v-model="caseData.short_description" class="form-control" id="description" required></textarea>
              </div>
              <div class="mb-3">
                <label for="questions" class="form-label">Вопросы для фидбека</label>
                <textarea v-model="caseData.questions" class="form-control" id="questions" required></textarea>
              </div>
              <div class="mb-3">
                <input type="checkbox" v-model="caseData.is_free" class="form-check-input" id="is_free">
                <label for="is_free" class="form-check-label">&nbsp;Бесплатный</label>
              </div>
              <div class="d-flex">
                <button type="submit" class="btn btn-primary ms-auto">{{ editingCase ? 'Сохранить' : 'Создать' }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Уведомления об ошибках или успешных действиях -->
    <div v-if="toastMessage" class="alert alert-info position-fixed bottom-0 end-0 p-3">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import axiosInstance from '../services/axiosInstance';

export default {
  data() {
    return {
      cases: [],
      loading: false,
      pagination: {
        page: 1,
        size: parseInt(localStorage.getItem('caseListPageSize')) || 10,
        total: 0,
        pages: 0
      },
      caseData: {
        title: '',
        description: '',
        short_description: '',
        questions: '',
        is_free: false,
      },
      editingCase: null,
      toastMessage: '',
      searchQuery: '',
      searchTimeout: null,
    };
  },
  computed: {
    visiblePages() {
      const current = this.pagination.page;
      const total = this.pagination.pages;
      const delta = 2; // Количество страниц слева и справа от текущей
      
      let start = Math.max(1, current - delta);
      let end = Math.min(total, current + delta);
      
      // Если мало страниц слева, добавляем справа
      if (current - delta <= 0) {
        end = Math.min(total, end + (delta - current + 1));
      }
      
      // Если мало страниц справа, добавляем слева
      if (current + delta >= total) {
        start = Math.max(1, start - (current + delta - total));
      }
      
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  methods: {
    async fetchCases() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          size: this.pagination.size
        };
        
        if (this.searchQuery && this.searchQuery.trim()) {
          params.search = this.searchQuery.trim();
        }
        
        const response = await axiosInstance.get('/cases', { params });
        
        this.cases = response.data.items;
        this.pagination.total = response.data.total;
        this.pagination.pages = response.data.pages;
      } catch (error) {
        this.showToast('Ошибка при загрузке кейсов');
        console.error('Error fetching cases:', error);
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.pages && page !== this.pagination.page) {
        this.pagination.page = page;
        this.fetchCases();
      }
    },
    changePageSize() {
      // Сохраняем размер страницы в localStorage
      localStorage.setItem('caseListPageSize', this.pagination.size.toString());
      this.pagination.page = 1; // Сбрасываем на первую страницу при изменении размера
      this.fetchCases();
    },
    openCreateModal() {
      this.caseData = { title: '', description: '', short_description: '', questions: '', is_free: false };
      this.editingCase = null;
      const modal = new Modal(document.getElementById('caseModal'));
      modal.show();
    },
    editCase(caseItem) {
      this.caseData = { ...caseItem };
      this.editingCase = caseItem.id;
      const modal = new Modal(document.getElementById('caseModal'));
      modal.show();
    },
    async submitCase() {
      try {
        if (this.editingCase) {
          await axiosInstance.put(`/cases/${this.editingCase}`, this.caseData);
          this.showToast('Кейс обновлен');
        } else {
          await axiosInstance.post('/cases', this.caseData);
          this.showToast('Кейс создан');
        }
        await this.fetchCases();
        const modal = Modal.getInstance(document.getElementById('caseModal'));
        modal.hide();
      } catch (error) {
        this.showToast('Ошибка при сохранении кейса');
        console.error('Error saving case:', error);
      }
    },
    confirmDelete(caseItem) {
      if (confirm(`Вы уверены, что хотите удалить кейс "${caseItem.title}"?`)) {
        this.deleteCase(caseItem.id);
      }
    },
    async deleteCase(caseId) {
      try {
        await axiosInstance.delete(`/cases/${caseId}`);
        this.showToast('Кейс удален');
        
        // Если удалили последний элемент на странице и это не первая страница, переходим на предыдущую
        if (this.cases.length === 1 && this.pagination.page > 1) {
          this.pagination.page--;
        }
        
        await this.fetchCases();
      } catch (error) {
        this.showToast('Ошибка при удалении кейса');
        console.error('Error deleting case:', error);
      }
    },
    showToast(message) {
      this.toastMessage = message;
      setTimeout(() => {
        this.toastMessage = '';
      }, 3000);
    },
    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    onSearchInput() {
      // Очищаем предыдущий таймер
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Устанавливаем новый таймер для debounce
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.fetchCases();
      }, 300); // Задержка 300ms
    },
    clearSearch() {
      this.searchQuery = '';
      this.pagination.page = 1;
      this.fetchCases();
    },
  },
  mounted() {
    this.fetchCases();
  },
};
</script>

<style>
.table td {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alert {
  z-index: 1050;
}
.pagination .page-link {
  cursor: pointer;
}
.pagination .page-item.disabled .page-link {
  cursor: not-allowed;
}
</style>
